import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { appointmentApi } from '@services/appointment.service'
import type { DoctorAvailabilityOverride } from '@/types'
import { Button } from '@components/ui/button'
import { Input } from '@components/ui/input'
import { Label } from '@components/ui/label'
import { Badge } from '@components/ui/badge'
import { Trash2, Palmtree, Plus } from 'lucide-react'
import { parseLocalDate, toISODate, formatDisplayDate } from './time-utils'

interface Props { doctorId: string }

interface VacationGroup {
  note: string | null
  startDate: string
  endDate: string
  days: number
  ids: string[]
}

function groupVacations(items: DoctorAvailabilityOverride[]): VacationGroup[] {
  const closed = [...items.filter((o) => o.is_closed)].sort((a, b) =>
    a.override_date.localeCompare(b.override_date)
  )
  if (!closed.length) return []

  const groups: VacationGroup[] = []
  let cur: VacationGroup = {
    note: closed[0].note ?? null,
    startDate: closed[0].override_date,
    endDate: closed[0].override_date,
    days: 1,
    ids: [closed[0].id],
  }

  for (let i = 1; i < closed.length; i++) {
    const o = closed[i]
    const diff =
      (parseLocalDate(o.override_date).getTime() - parseLocalDate(cur.endDate).getTime()) /
      86400000
    if (diff === 1 && (o.note ?? null) === cur.note) {
      cur.endDate = o.override_date
      cur.days += 1
      cur.ids.push(o.id)
    } else {
      groups.push(cur)
      cur = { note: o.note ?? null, startDate: o.override_date, endDate: o.override_date, days: 1, ids: [o.id] }
    }
  }
  groups.push(cur)
  return groups
}

export function VacationsTab({ doctorId }: Props) {
  const queryClient = useQueryClient()
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [note, setNote] = useState('')

  const { data } = useQuery({
    queryKey: ['availability-overrides', doctorId],
    queryFn: () => appointmentApi.getAvailabilityOverrides(doctorId),
  })

  const groups = groupVacations(data?.items ?? [])
  const today = new Date().toISOString().slice(0, 10)
  const upcoming = groups.filter((g) => g.endDate >= today)
  const past = groups.filter((g) => g.endDate < today)

  const addMutation = useMutation({
    mutationFn: async () => {
      const start = parseLocalDate(startDate)
      const end = parseLocalDate(endDate)
      if (start > end) throw new Error('invalid_range')
      const current = new Date(start)
      while (current <= end) {
        await appointmentApi.createAvailabilityOverride({
          doctor_id: doctorId,
          override_date: toISODate(current),
          is_closed: true,
          note: note || undefined,
        })
        current.setDate(current.getDate() + 1)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability-overrides', doctorId] })
      setStartDate('')
      setEndDate('')
      setNote('')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) await appointmentApi.deleteAvailabilityOverride(id)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['availability-overrides', doctorId] }),
  })

  const days =
    startDate && endDate
      ? Math.max(
          0,
          Math.round(
            (parseLocalDate(endDate).getTime() - parseLocalDate(startDate).getTime()) / 86400000
          ) + 1
        )
      : 0

  return (
    <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
      <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
        <div className="border-b bg-violet-500/5 px-4 py-2.5" style={{ borderTopColor: '#7c3aed', borderTopWidth: '2px' }}>
          <h3 className="text-sm font-semibold text-violet-900">Registrar ausencia</h3>
        </div>
        <div className="space-y-3 p-4">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Fecha inicio</Label>
              <Input
                type="date"
                value={startDate}
                min={today}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Fecha fin</Label>
              <Input
                type="date"
                value={endDate}
                min={startDate || today}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Motivo</Label>
            <Input
              placeholder="Ej: Vacaciones, Congreso, Baja médica"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="h-8 text-sm"
            />
          </div>

          {days > 0 && (
            <p className="text-xs text-muted-foreground">
              Se crearán <strong>{days}</strong> día{days !== 1 ? 's' : ''} de ausencia
            </p>
          )}

          {addMutation.isError && (
            <p className="text-xs text-destructive">
              Verifica que la fecha de inicio sea antes de la fecha de fin.
            </p>
          )}

          <Button
            size="sm"
            className="w-full"
            onClick={() => addMutation.mutate()}
            disabled={!startDate || !endDate || addMutation.isPending}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            {addMutation.isPending ? 'Guardando…' : 'Registrar ausencia'}
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {groups.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed bg-card px-6 py-12 text-center">
            <Palmtree className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm font-medium">Sin ausencias registradas</p>
            <p className="text-xs text-muted-foreground">
              Registra vacaciones o períodos de ausencia para que no aparezcan como disponibles
            </p>
          </div>
        ) : (
          <>
            {upcoming.length > 0 && (
              <GroupList
                label="Próximas ausencias"
                groups={upcoming}
                onDelete={(ids) => deleteMutation.mutate(ids)}
              />
            )}
            {past.length > 0 && (
              <GroupList
                label="Ausencias pasadas"
                groups={past}
                onDelete={(ids) => deleteMutation.mutate(ids)}
                muted
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

function GroupList({
  label,
  groups,
  onDelete,
  muted = false,
}: {
  label: string
  groups: VacationGroup[]
  onDelete: (ids: string[]) => void
  muted?: boolean
}) {
  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <div
        className={`divide-y overflow-hidden rounded-xl border bg-card shadow-sm ${muted ? 'opacity-60' : ''}`}
      >
        {groups.map((g, idx) => (
          <div key={idx} className="flex items-center gap-3 bg-violet-500/5 px-4 py-3.5">
            <Palmtree className="h-4 w-4 shrink-0 text-violet-500/70" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">
                {g.startDate === g.endDate
                  ? formatDisplayDate(g.startDate, { weekday: 'long', day: 'numeric', month: 'long' })
                  : `${formatDisplayDate(g.startDate, { day: 'numeric', month: 'short' })} – ${formatDisplayDate(g.endDate, { day: 'numeric', month: 'long', year: 'numeric' })}`}
              </p>
              <p className="text-xs text-muted-foreground">
                {g.days} día{g.days !== 1 ? 's' : ''}
                {g.note && ` · ${g.note}`}
              </p>
            </div>
            {g.note && (
              <Badge variant="outline" className="shrink-0 border-border/60 text-xs text-muted-foreground">
                {g.note}
              </Badge>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
              onClick={() => onDelete(g.ids)}
              title="Eliminar período"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  )
}
